// Deep import, not the barrel: `@repo/common` re-exports the AWS SDK clients,
// which resolve to ESM under the browser export condition and break any jsdom
// component test that renders a card.
import { formatPayStructured } from "@repo/common/opportunity-pay";
import { formatReferralEstimateUsd } from "@repo/common/pay-display";
import {
  DEFAULT_REFERRAL_COMMISSION_BPS,
  REFERRAL_COMMISSION_CAP_AMOUNT,
} from "@repo/db/constants";

import type { Platform } from "@/hooks/use-platform";

import { formatCurrency } from "./conversion-utils";

/**
 * Referral economics for display. Re-exported from the same constants the
 * payout path reads rather than mirrored, so a rate change cannot leave the
 * copy quoting a number we no longer pay.
 *
 * The rate is a floor, not a flat rate: a listing can advertise more for the
 * study its link was shared for. Quote a listing's own `commissionBps` where
 * one is in hand and keep this for the no-listing case.
 */
export const REFERRAL_DISPLAY_COMMISSION_BPS = DEFAULT_REFERRAL_COMMISSION_BPS;
export const REFERRAL_DISPLAY_CAP_AMOUNT = REFERRAL_COMMISSION_CAP_AMOUNT;

export type DeviceType =
  | "MOBILE_IOS"
  | "MOBILE_ANDROID"
  | "TABLET_IOS"
  | "TABLET_ANDROID"
  | "DESKTOP"
  | "OTHER";

const ALL_DEVICE_TYPES: DeviceType[] = [
  "MOBILE_IOS",
  "MOBILE_ANDROID",
  "TABLET_IOS",
  "TABLET_ANDROID",
  "DESKTOP",
  "OTHER",
];

/**
 * navigator.userAgent doesn't reliably distinguish iPad from iPhone or an
 * Android tablet from an Android phone (usePlatform only resolves to
 * `ios | android | desktop`). So instead of asking "is the participant's
 * single DeviceType in the allow list?" we ask "could any device in the
 * allow list plausibly match this platform?"; returning true when at
 * least one phone-or-tablet variant of the same family is allowed. This
 * keeps us from falsely flagging iPad users as excluded from a
 * TABLET_IOS-only study.
 */
function currentPlatformCouldMatch(
  allowedDeviceTypes: DeviceType[],
  platform: Platform,
): boolean {
  switch (platform) {
    case "ios":
      return allowedDeviceTypes.some(
        (d) => d === "MOBILE_IOS" || d === "TABLET_IOS",
      );
    case "android":
      return allowedDeviceTypes.some(
        (d) => d === "MOBILE_ANDROID" || d === "TABLET_ANDROID",
      );
    case "desktop":
      return allowedDeviceTypes.includes("DESKTOP");
  }
}

/**
 * Describes whether a study's device restriction excludes the participant's
 * current platform, and if so, what device they should switch to. Returns
 * null when the study has no effective restriction (all 6 types allowed) or
 * when the current platform plausibly matches an allowed device.
 */
export function computeDeviceRestriction(
  allowedDeviceTypes: DeviceType[] | null | undefined,
  platform: Platform,
): { label: string } | null {
  if (!allowedDeviceTypes || allowedDeviceTypes.length === 0) {
    return null;
  }
  if (allowedDeviceTypes.length >= ALL_DEVICE_TYPES.length) {
    return null;
  }
  if (currentPlatformCouldMatch(allowedDeviceTypes, platform)) {
    return null;
  }

  const onlyMobile = allowedDeviceTypes.every((d) =>
    ["MOBILE_IOS", "MOBILE_ANDROID", "TABLET_IOS", "TABLET_ANDROID"].includes(
      d,
    ),
  );
  const onlyDesktop = allowedDeviceTypes.every((d) => d === "DESKTOP");
  const onlyIOS = allowedDeviceTypes.every(
    (d) => d === "MOBILE_IOS" || d === "TABLET_IOS",
  );
  const onlyAndroid = allowedDeviceTypes.every(
    (d) => d === "MOBILE_ANDROID" || d === "TABLET_ANDROID",
  );

  if (onlyIOS) {
    return { label: "iOS only" };
  }
  if (onlyAndroid) {
    return { label: "Android only" };
  }
  if (onlyMobile) {
    return { label: "Mobile only" };
  }
  if (onlyDesktop) {
    return { label: "Desktop only" };
  }
  return { label: "Not for this device" };
}

export {
  advertisedPayInput,
  formatPayCardDisplay,
  type PayCardDisplay,
  resolvedPayInput,
} from "@repo/common/opportunity-pay";

/**
 * Fixed "Contract & Payment Terms" bullets rendered on every opportunity
 * listing surface (public detail, expert panel, internal preview). The payment
 * cadence line is the one variable: ONE_TIME studies release payment on
 * approval (see process-submission-payout, which only pays out ONE_TIME on
 * approval), while HOURLY / PER_TASK studies settle on the weekly batch.
 * `payRateFrequency` is the study's StudyPayRateFrequency, joined onto the
 * listing as the pay-cadence source of truth.
 */
export function getContractPaymentTerms(
  payRateFrequency: string | null | undefined,
): string[] {
  const paymentLine =
    payRateFrequency === "ONE_TIME"
      ? "Payment is issued as soon as your work is approved."
      : "Payments are processed weekly based on services rendered.";

  return [
    "You will be engaged as an independent contractor.",
    "This is a fully remote opportunity that can be completed on your own schedule.",
    "Opportunities can be extended, shortened, or concluded early depending on needs and performance.",
    "Your participation will not involve access to confidential or proprietary information from any employer, client, or institution.",
    paymentLine,
    "We are unable to support H1-B or STEM OPT candidates at this time.",
  ];
}

export function formatWorkMode(mode: string): string {
  switch (mode) {
    case "REMOTE":
      return "Remote";
    case "HYBRID":
      return "Hybrid";
    case "ON_SITE":
      return "On-site";
    default:
      return mode;
  }
}

export interface ReferralRateCopy {
  /** Percentage for the hero figure. */
  heroPercent: number;
  /** Completes "{heroPercent}% ..." under the hero. */
  heroCaption: string;
  bulletTitle: string;
  bulletSubtitle: string;
  /** Full sentence for the dialog description and screen readers. */
  sentence: string;
}

/**
 * Copy for a referral offer's rate.
 *
 * A listing's rate applies only to the study its link was shared for;
 * everything else that person goes on to do pays the platform default
 * (`commissionBpsForStudy` in the referrals service). So an offer's own rate
 * can never be quoted as a plain "you earn N%" without scoping it to the
 * study, in either direction: an elevated listing would over-promise on every
 * other study, and a listing below the default (0 is settable) would headline
 * a figure lower than what the referrer actually earns on the rest.
 *
 * The hero never drops below the default for that reason: it is the rate that
 * holds across Terac, and the sentence carries the study-specific exception.
 *
 * Pass null when no offer is in hand, which quotes the default as a floor.
 */
export function resolveReferralRateCopy(
  commissionBps: number | null,
): ReferralRateCopy {
  const defaultPercent = REFERRAL_DISPLAY_COMMISSION_BPS / 100;

  if (commissionBps === null) {
    return {
      heroPercent: defaultPercent,
      heroCaption: "of what your referrals are paid.",
      bulletTitle: `From ${defaultPercent}% of their pay`,
      bulletSubtitle: "Some studies pay a higher referral rate.",
      sentence: `Earn at least ${defaultPercent}% of what your referrals are paid on Terac.`,
    };
  }

  const offerPercent = commissionBps / 100;

  if (offerPercent === defaultPercent) {
    return {
      heroPercent: defaultPercent,
      heroCaption: "of what your referrals are paid.",
      bulletTitle: `${defaultPercent}% of their pay`,
      bulletSubtitle: "Every study they complete, every time.",
      sentence: `Earn ${defaultPercent}% of what your referrals are paid on Terac.`,
    };
  }

  if (offerPercent > defaultPercent) {
    return {
      heroPercent: offerPercent,
      heroCaption: "of what they are paid on this study.",
      bulletTitle: `${offerPercent}% on this study`,
      bulletSubtitle: `${defaultPercent}% on everything else they do on Terac.`,
      sentence: `Earn ${offerPercent}% of what your referrals are paid on this study, and ${defaultPercent}% on anything else they do on Terac.`,
    };
  }

  return {
    heroPercent: defaultPercent,
    heroCaption: "of what your referrals are paid on Terac.",
    bulletTitle: `${defaultPercent}% of their pay`,
    bulletSubtitle: `This study pays ${offerPercent}%.`,
    sentence: `Earn ${defaultPercent}% of what your referrals are paid on Terac. This study pays ${offerPercent}%.`,
  };
}

/** What a referral estimate is per, where the pay is not one amount. */
const REFERRAL_ESTIMATE_PER: Record<string, string> = {
  HOURLY: "per hour worked",
  PER_TASK: "per task",
};

export interface ReferralEstimate {
  /** The referrer's cut of one unit of the referred person's pay. */
  amount: string;
  /** The unit that cut is per, or null where the pay is one amount. */
  per: string | null;
}

/**
 * `figure` is the joiner's (`joinerRateOf`), never the viewer's own row. The
 * standard rate, not the listing's, so a mixed feed never implies a study's
 * elevated rate applies to the others.
 */
export function formatReferralEstimate(
  figure: { rateUsd: string; payRateFrequency: string } | null | undefined,
): ReferralEstimate | null {
  const amount = figure
    ? formatReferralEstimateUsd(
        Number(figure.rateUsd),
        REFERRAL_DISPLAY_COMMISSION_BPS,
      )
    : null;
  if (!figure || !amount) {
    return null;
  }
  return {
    amount,
    per: REFERRAL_ESTIMATE_PER[figure.payRateFrequency] ?? null,
  };
}

/** Absent where a referral earns nothing, to mirror `formatReferralEstimate`. */
export function formatReferralCapFor(
  figure: { rateUsd: string } | null | undefined,
  capAmount: number | null,
): string | null {
  return capAmount !== null && Number(figure?.rateUsd) > 0
    ? formatCurrency(capAmount)
    : null;
}

/**
 * Short engagement badge beside the work-mode pill on the detail hero. Mirrors
 * the displayed pay cadence when a rate is known; otherwise falls back to the
 * category field so a rate-less listing never asserts a wrong cadence, and
 * returns null (badge hidden) when neither is known.
 */
export function formatEngagementBadge(
  input: Parameters<typeof formatPayStructured>[0],
): string | null {
  const frequency = formatPayStructured(input)?.frequency;
  if (frequency === "HOURLY") {
    return "Hourly";
  }
  if (frequency === "PER_TASK") {
    return "Per task";
  }
  if (frequency === "ONE_TIME") {
    return "One-time";
  }
  if (input.category === "ONE_TIME") {
    return "One-time";
  }
  if (input.category === "PROJECT_BASED") {
    return "Ongoing";
  }
  return null;
}

export function formatOpportunityType(type: string): string {
  switch (type) {
    case "INTERVIEW":
      return "Voice Interview";
    case "SURVEY":
      return "Survey";
    case "USABILITY_TEST":
      return "Usability Test";
    case "FOCUS_GROUP":
      return "Focus Group";
    default:
      return type;
  }
}

// -- Participant Submission Status --

export type ParticipantSubmissionStatus =
  | "IN_PROGRESS"
  | "PROCESSING"
  | "AWAITING_REVIEW"
  | "APPROVED"
  | "PAID"
  | "SCREENED_OUT"
  | "NOT_SELECTED"
  | "EXPIRED";

/**
 * Whether this participation status should hide the listing from browse.
 * Keep only true in-progress items in browse. Any completed/terminal state
 * should be hidden from browse and shown in dedicated status tabs instead.
 */
export function shouldHideFromBrowse(
  status: ParticipantSubmissionStatus | undefined,
): boolean {
  return Boolean(status && status !== "IN_PROGRESS");
}

export function deriveSubmissionStatus(
  interviewStatus: string,
  paymentStatus: string | null,
): ParticipantSubmissionStatus {
  switch (interviewStatus) {
    case "ACTIVE":
    case "AWAITING_SCREENING":
      return "IN_PROGRESS";
    case "PROCESSING":
      return "PROCESSING";
    case "AWAITING_REVIEW":
      return "AWAITING_REVIEW";
    case "APPROVED":
      return paymentStatus === "FULFILLED" ? "PAID" : "APPROVED";
    case "SCREENED_OUT":
      return "SCREENED_OUT";
    case "REJECTED":
      return "NOT_SELECTED";
    case "TIMED_OUT":
    case "FAILED":
      return "EXPIRED";
    // Submission states (from apply flow)
    case "SCREENING":
    case "SCREEN_PASSED":
    case "IN_PROGRESS":
      return "IN_PROGRESS";
    default:
      return "IN_PROGRESS";
  }
}

export function getSubmissionStatusDisplay(
  status: ParticipantSubmissionStatus,
): {
  label: string;
  className: string;
  tooltip: string;
} {
  switch (status) {
    case "IN_PROGRESS":
      return {
        label: "In Progress",
        className: "text-blue-600",
        tooltip: "Your interview is currently in progress.",
      };
    case "PROCESSING":
      return {
        label: "Processing",
        className: "text-amber-600",
        tooltip:
          "Your interview is being processed. This usually takes a few minutes.",
      };
    case "AWAITING_REVIEW":
      return {
        label: "Awaiting Review",
        className: "text-amber-600",
        tooltip:
          "Your interview is complete and is being reviewed by the research team.",
      };
    case "APPROVED":
      return {
        label: "Approved",
        className: "text-emerald-600",
        tooltip:
          "Your submission has been approved. Payment will be processed shortly.",
      };
    case "PAID":
      return {
        label: "Paid",
        className: "text-emerald-600",
        tooltip: "Payment has been sent to your account.",
      };
    case "SCREENED_OUT":
      return {
        label: "Not a match",
        className: "text-red-600",
        tooltip:
          "Your screening responses didn't match the requirements for this opportunity.",
      };
    case "NOT_SELECTED":
      return {
        label: "Not Selected",
        className: "text-red-600",
        tooltip: "You were not selected for this opportunity.",
      };
    case "EXPIRED":
      return {
        label: "Expired",
        className: "text-red-600",
        tooltip: "This opportunity has expired.",
      };
  }
}
